import React, { Fragment, useEffect, useState } from 'react';
import {SzButton, SzSelect, SzTypographie, SzInput} from '@suezenv/react-theme-components'
import {ISignalListAdvancedFilter,IOptions, ISignalAdvancedFavoriteFilters, ISelectedFilters, ISignalFilter} from "../../../../type/signal.type";
import './signal-advanced-filter.scss';
import { useTranslationWithPrefix } from '../../../../hooks/translation';
import { ObjectHelper } from "../../../../helpers/ObjectHelper";
import AddressFilter from './filters/addressFilter';
import SaveFilterModal from './modals/save-filter-modal';
import FavoriteFilters from './signal-favorite-filter';
import { ADVANCED_FILTERS_TYPES } from "../../../../constants/filters";

export const SignalAdvancedFilter: React.FC<ISignalListAdvancedFilter> = ({
    filters,
	defaultFilters,
    externalFilters,
    setFieldValue,
    onClose,
	favoriteFiltersList,
	setFavoriteFiltersList,
	formValues,
	selectedFavoriteFilter,
	setSelectedFavoriteFilter
}) => {
	const trans = useTranslationWithPrefix("signalement", "form_filter");
	const getDefaultSelectedFilter = () => [{ id: Date.now(), key: null, label: null }];

	const [filtersListOptions, setFiltersListOptions] = useState<IOptions[]>([]);
	const [selectedFilters, setSelectedFilters] = useState<ISelectedFilters[]>(getDefaultSelectedFilter());
	const [incrementalFilterId, setIncrementalFilterId] = useState(0);
	const [selectedFavoriteFilterOption, setSelectedFavoriteFilterOption] = useState<ISignalAdvancedFavoriteFilters | null>();
	const [showSaveModal, setShowSaveModal] = useState(false);
	const [isReset, setIsReset] = useState(false);



	const initFilters = (filters: ISignalFilter) => {
		initFiltersListOptions();

		const newFilters:any[] = []
		let incrementalFilterId = 0;

		Object.entries(filters).forEach(([key, filter]: [string, any]) => {
			const isArrayWithLength = Array.isArray(filter) && filter.length > 0;
			const isStringWithLength = typeof filter === "string" && filter.trim().length > 0;
			const isObjectWithKeys = typeof filter === "object" && filter !== null && Object.keys(filter).length > 0;
			const isNumberFilter = typeof filter === "number" && !isNaN(filter);

			if ((isArrayWithLength || isObjectWithKeys || isNumberFilter || isStringWithLength)
				&& Object.prototype.hasOwnProperty.call(externalFilters, key)
			) {
				newFilters.push({
					id: Date.now() + incrementalFilterId,
					key: key,
					label: externalFilters[key]?.label
				});
				incrementalFilterId++;
			}
		});
		setIncrementalFilterId(incrementalFilterId);
		setSelectedFilters(newFilters.length > 0 ? newFilters : getDefaultSelectedFilter());
	}

	const initFiltersListOptions = () => {
		const filtersListOptions = Object.keys(externalFilters)
			.filter(key => key !== ADVANCED_FILTERS_TYPES.RADIUS) // radius filter is added dynamically when address filter is selected
			.map(key => ({
				id: key,
				label: externalFilters[key].label,
				value: externalFilters[key].label
			}));
		setFiltersListOptions(filtersListOptions);
	}

	const resetFilters = () => {
		setIsReset(true);
		resetFieldsValue();
		setSelectedFavoriteFilterOption(null);
		if (defaultFilters) {
			initFilters(defaultFilters);
			return;
		}
		initFiltersListOptions();
		setSelectedFilters(getDefaultSelectedFilter());		
	}

	const resetFieldsValue = (filterKey?: string | null) => {
		if (!filterKey) {
			if (defaultFilters) {
				setFieldValue(ADVANCED_FILTERS_TYPES.STATUS, defaultFilters.status || []);
				setFieldValue(ADVANCED_FILTERS_TYPES.REQUEST_TYPES, defaultFilters.requestTypes || []);
				setFieldValue(ADVANCED_FILTERS_TYPES.ADDRESS, defaultFilters.address || undefined);
				setFieldValue(ADVANCED_FILTERS_TYPES.RADIUS, defaultFilters.radius || undefined);
			} else {
				setFieldValue(ADVANCED_FILTERS_TYPES.STATUS, []);
				setFieldValue(ADVANCED_FILTERS_TYPES.REQUEST_TYPES, []);
				setFieldValue(ADVANCED_FILTERS_TYPES.ADDRESS, undefined);
				setFieldValue(ADVANCED_FILTERS_TYPES.RADIUS, undefined);
			}
		}
		else if (ADVANCED_FILTERS_TYPES.ADDRESS === filterKey) {
			setFieldValue(ADVANCED_FILTERS_TYPES.ADDRESS, undefined);
		}
		else if (ADVANCED_FILTERS_TYPES.RADIUS === filterKey) {
			setFieldValue(ADVANCED_FILTERS_TYPES.RADIUS, undefined);
		}
		else {
			setFieldValue(filterKey, undefined);
		}
	}

	const handleFilterChange = (selectedOption: IOptions, idx: number) => {
		const newSelectedFilter = [...selectedFilters];
		setFiltersListOptions(prevFilters => {
			const filtered = prevFilters
			if (newSelectedFilter[idx].label != null &&
				newSelectedFilter[idx].label !== selectedOption.value) {
				filtered.push({
					id: newSelectedFilter[idx].key ?? '',
					label: newSelectedFilter[idx].label ?? '',
					value: newSelectedFilter[idx].label ?? ''
				});
				resetFieldsValue(newSelectedFilter[idx].key);
			}
			return filtered;
		});
		newSelectedFilter[idx].label = selectedOption.value;
		newSelectedFilter[idx].key = selectedOption.id;
		newSelectedFilter[idx].id = Date.now();

		setFieldValue(selectedOption.id, externalFilters[selectedOption.id].defaultValue);
		setSelectedFilters(newSelectedFilter);
	};

	const removeFilter = (idx: number) => {
		const removedFilter = selectedFilters[idx];
		resetFieldsValue(removedFilter.key);

		setFiltersListOptions(prevFilters => { //add the removed filter to the select filter list
			const newFiltersListOptions = [...prevFilters];
			newFiltersListOptions.push({
				id: removedFilter.key ?? '',
				label: removedFilter.label ?? '',
				value: removedFilter.label ?? ''
			});
			return newFiltersListOptions;
		});

		if (selectedFilters.length <= 1) {
			setSelectedFilters(getDefaultSelectedFilter());
		}
		else {
			let selectedFiltersUpdated = ObjectHelper.deleteSpecificFilterFromList(idx, selectedFilters)
			if (removedFilter.key === ADVANCED_FILTERS_TYPES.ADDRESS
				&& selectedFiltersUpdated.findIndex(f => f.key === ADVANCED_FILTERS_TYPES.RADIUS) !== -1
			) { // if address filter is removed, also remove radius filter
				selectedFiltersUpdated = ObjectHelper.deleteSpecificFilterFromList(
					selectedFiltersUpdated.findIndex(f => f.key === ADVANCED_FILTERS_TYPES.RADIUS),
					selectedFiltersUpdated
				);
				resetFieldsValue(ADVANCED_FILTERS_TYPES.RADIUS);
			}
			if (selectedFiltersUpdated.length < 1) {
				setSelectedFilters(getDefaultSelectedFilter());
			} else {
				setSelectedFilters(selectedFiltersUpdated);
			}
		}
		setSelectedFavoriteFilterOption(null);
	}


	const handleDynamicChange = (filterKey: string, event: any) => {
		setSelectedFavoriteFilterOption(null);
		if (!event) {
			resetFieldsValue(filterKey);
			return
		}
		if (filterKey === ADVANCED_FILTERS_TYPES.ADDRESS && typeof event === 'object') {
			setFieldValue(ADVANCED_FILTERS_TYPES.ADDRESS, event);
			return;
		}
		if (filterKey === ADVANCED_FILTERS_TYPES.RADIUS ){ 
			if (/^\d*$/.test(event)) { // Check if the input is a number between 1 and 1000
				const newRadius = Math.max(1, Math.min(1000, Number(event)));
				setFieldValue(ADVANCED_FILTERS_TYPES.RADIUS, newRadius);
			}
			return;
		}
		setFieldValue(filterKey, event);
	};

	const getSelectedFilterValue = (filterKey: string ) => {
		const componentType = externalFilters[filterKey]?.type;

		if (filterKey === ADVANCED_FILTERS_TYPES.ADDRESS) {
			return (
				<AddressFilter
					value={getDefaultValueForSelectComponent(filterKey)}
					onChange={(event) => { handleDynamicChange(filterKey, event) }}
					placeholder={externalFilters[filterKey].label}
					geographicalPriority={externalFilters[filterKey]?.geographicalPriority}
					searchAddressFilters={externalFilters[filterKey]?.searchAddressFilters}
				/>
			);
		}
		else if (componentType === "input") {
			return (
				<SzInput
					placeholder={externalFilters[filterKey].label}
					variant="outlined"
					value={formValues[filterKey] ?? getDefaultValueForSelectComponent(filterKey)}
					onChange={(event: React.ChangeEvent<HTMLInputElement>) => { handleDynamicChange(filterKey, event.target.value) }}
				/>
			)
		}
		else if (componentType === "select") {
			return (
				<SzSelect
					id={`select-${filterKey}`}
					className='mb-3 border-secondary'
					name={filterKey}
					placeholder={externalFilters[filterKey].label}
					options={externalFilters[filterKey].options}
					defaultValue={getDefaultValueForSelectComponent(filterKey)}
					isClearable={externalFilters[filterKey]?.isClearable ?? false}
					isMulti={externalFilters[filterKey]?.isMulti ?? false}
					isMultiList={externalFilters[filterKey]?.isMultiList ?? false}
					onChange={(event: React.ChangeEvent<HTMLInputElement>) => { handleDynamicChange(filterKey, event) }}
				/>
			)
		}	
		return null;
	};

	const getDefaultValueForSelectComponent = (filterKey: string) => {
		
		if (selectedFavoriteFilterOption) {
			if (filterKey === ADVANCED_FILTERS_TYPES.ADDRESS) {
				return selectedFavoriteFilterOption.address?.address ?? selectedFavoriteFilterOption.address ?? '';
			}
			return selectedFavoriteFilterOption?.[filterKey] ?? externalFilters[filterKey].defaultValue;
		}
		return isReset ? defaultFilters[filterKey] : externalFilters[filterKey].defaultValue;
	}

	const saveNewFavoriteFilters = (favoriteFilterLabel: string) => {
		const { requestTypes, status, address, radius } = formValues;
		const newFavoriteFilter: ISignalAdvancedFavoriteFilters = {
			requestTypes: requestTypes,
			status: status,
			address: address,
			radius: radius,
			label: favoriteFilterLabel
		};
		setFavoriteFiltersList([...favoriteFiltersList, newFavoriteFilter]);
		setSelectedFavoriteFilterOption({...formValues, label: favoriteFilterLabel});
	};

	const availableFilters = () => {
		setFiltersListOptions(prevFilters => {
			const filtered = prevFilters.filter(item => !selectedFilters.some(selected => selected.label === item.label));
			const hasAddress = selectedFilters.some(f => f.key === ADVANCED_FILTERS_TYPES.ADDRESS);
			if (hasAddress 
				&& externalFilters[ADVANCED_FILTERS_TYPES.RADIUS] 
				&& !filtered.find(f => f.id === ADVANCED_FILTERS_TYPES.RADIUS)
				&& !selectedFilters.find(f => f.key === ADVANCED_FILTERS_TYPES.RADIUS)
			) {
				filtered.push({
					id: ADVANCED_FILTERS_TYPES.RADIUS,
					label: externalFilters[ADVANCED_FILTERS_TYPES.RADIUS].label,
					value: externalFilters[ADVANCED_FILTERS_TYPES.RADIUS].label
				});
			 } 
			else if (!hasAddress) {
				const radiusIndex = filtered.findIndex(f => f.id === ADVANCED_FILTERS_TYPES.RADIUS);
				if (radiusIndex !== -1) {
					filtered.splice(radiusIndex, 1);
				}
			}
			return filtered;
		});
	};

	const applyFavoriteFilter = () => {
		if (selectedFavoriteFilterOption) {
			resetFieldsValue();
			initFiltersListOptions();

			const newFilters: any[] = []
			let incrementalFilterId = 0;

			Object.entries(selectedFavoriteFilterOption).forEach(([key, filter]: [string, any]) => {
				const isArrayWithLength = Array.isArray(filter) && filter.length > 0;
				const isStringWithLength = typeof filter === "string" && filter.trim().length > 0;
				const isObjectWithKeys = typeof filter === "object" && filter !== null && Object.keys(filter).length > 0;
				const isNumberFilter = typeof filter === "number" && !isNaN(filter);

				if ((isArrayWithLength || isObjectWithKeys || isNumberFilter || isStringWithLength)
					&& Object.prototype.hasOwnProperty.call(externalFilters, key)
				) {
					newFilters.push({
						id: Date.now() + incrementalFilterId,
						key: key,
						label: externalFilters[key]?.label
					});
					incrementalFilterId++;
					if (key === ADVANCED_FILTERS_TYPES.ADDRESS && typeof filter === 'object') {
						setFieldValue(ADVANCED_FILTERS_TYPES.ADDRESS, filter);
					}
					else if (key === ADVANCED_FILTERS_TYPES.RADIUS) {
						if (/^\d*$/.test(filter)) { // Check if the input is a number between 1 and 1000
							const newRadius = Math.max(1, Math.min(1000, Number(filter)));
							setFieldValue(ADVANCED_FILTERS_TYPES.RADIUS, newRadius);
						}
					}
					else {
						setFieldValue(key, filter);
					}
				}
			});
			//add requestTypes filter if not present in the selected favorite filter because of default empty value
			if (filters.requestTypes.length > 0 && !selectedFavoriteFilterOption.requestTypes) {
				newFilters.push({
					id: Date.now() + incrementalFilterId,
					key: ADVANCED_FILTERS_TYPES.REQUEST_TYPES,
					label: externalFilters[ADVANCED_FILTERS_TYPES.REQUEST_TYPES]?.label
				});
			}
			if (newFilters.length > 0) {
				setSelectedFilters(newFilters);
			}
		}
	}

	useEffect(() => {
		initFilters(filters);
	}, []);

	useEffect(() => {
		availableFilters();
	}, [selectedFilters]);

	useEffect(() => {
		applyFavoriteFilter();
	}, [selectedFavoriteFilterOption]);

	return (
		<div  className="filter-panel bg-white shadow-sm p-3">
			<div className="filter-header row">
				<div className="d-flex justify-content-start col-1">
					<SzTypographie variant="body" size="L" className="mb-3">
						{trans('advanced_filters.title')}
					</SzTypographie>
				</div>
				<div className="d-flex justify-content-end align-items-center col-11">
					<FavoriteFilters
						selectedFavoriteFilterOption={selectedFavoriteFilterOption}
						setSelectedFavoriteFilterOption={setSelectedFavoriteFilterOption}
						favoriteFiltersList={favoriteFiltersList}
						setFavoriteFiltersList={setFavoriteFiltersList}
						selectedFavoriteFilter={selectedFavoriteFilter}
						setSelectedFavoriteFilter={setSelectedFavoriteFilter}
					/>
					<SzButton variant="tertiary" className="mr-3" icon="synchronize-arrow-1" onClick={() => {
						resetFilters();
					}}>
						{trans('advanced_filters.reset')}
					</SzButton>
				</div>
			</div>
			<div className="filter-body">
				{selectedFilters.map((filter, idx) => {
					let selectedValue;
					if (filter.key) {
						selectedValue = getSelectedFilterValue(filter.key);
					}
					return (
						<div className="row" key={filter.id}>
							<div className="col-5 pr-0">
								<SzSelect
									key={filter.id}
									id='filter-select'
									className='mb-3 border-secondary'
									name={'filter-select'}
									placeholder={trans('advanced_filters.select_filter')}
									onChange={(option: IOptions) => handleFilterChange(option, idx)}
									options={filtersListOptions}
									defaultValue={filter.key ? { id: filter.key, label: filter.label, value: filter.label } : null}
									isSearchable={true}
								/>
							</div>
							{selectedValue ? (
								<Fragment>
									<div className="col-6 pl-0">
										{selectedValue}
									</div>
									<div className="col-1 d-flex justify-content-center">
										<SzButton variant="tertiary" className="mr-3" icon="bin-2-alternate" onClick={() => removeFilter(idx)} />
									</div>
								</Fragment>
							) : (
								<div className="col-6 pl-0">
									<SzInput
										placeholder={trans('advanced_filters.filter_value_placeholder')}
										variant="outlined"
										disabled
									/>
								</div>
							)}
						</div>
					);
				})}
			<SzButton variant="tertiary" isDisabled={selectedFilters.length < Object.keys(externalFilters).length ? false : true} className="mr-3" icon="add-square" onClick={() => {
				setSelectedFilters([...selectedFilters, {id: Date.now() + incrementalFilterId, key: null, label: null}]);
				setIncrementalFilterId(incrementalFilterId + 1);
				setSelectedFavoriteFilterOption(null);
			}}>
				{trans('advanced_filters.add_filter')}
			</SzButton>
			</div>
			<div className="filter-footer top-border" style={{ position: "relative" }}>
				<SzButton variant="tertiary" className="mr-3" onClick={onClose}>
					{trans('advanced_filters.cancel')}
				</SzButton>
				<div className="d-flex ml-auto">
					<SzButton variant="tertiary" className="ml-auto" onClick={() => setShowSaveModal(true)}>
						{trans('advanced_filters.save')}
					</SzButton>
					<SzButton variant="secondary"  type='submit' onClick={() => setSelectedFavoriteFilter(selectedFavoriteFilterOption ?? null) }>
						{trans('advanced_filters.apply')}
					</SzButton>
					<SaveFilterModal
						setShowSaveModal={setShowSaveModal}
						showSaveModal={showSaveModal}
						saveNewFavoriteFilters={saveNewFavoriteFilters}
					/> 
				</div>
			</div>
		</div>
	);
};

export default SignalAdvancedFilter;